import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import type { FormField } from '../../../interfaces/form.interface';
import { InputType } from '../../../interfaces/input.interface';

@Component({
    selector: 'app-reactive-read-only-field',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './read-only-field.component.html',
    styleUrls: ['./read-only-field.component.scss'],
})
export class ReactiveReadOnlyFieldComponent<T> implements OnChanges, OnDestroy, DoCheck {
    @Input() field!: FormField<T>;

    @Input() model!: FormGroup;

    public readonly inputType = InputType;

    public displayValue = '-';

    private valueChangesSubscription?: Subscription;

    private lastRawValue: unknown;

    public constructor(private readonly cdr: ChangeDetectorRef) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['field'] || changes['model']) {
            this.bindControlValue();
        }
    }

    public ngOnDestroy(): void {
        this.valueChangesSubscription?.unsubscribe();
    }

    public ngDoCheck(): void {
        this.refreshDisplayFromControl(false);
    }

    private bindControlValue(): void {
        this.valueChangesSubscription?.unsubscribe();

        const control = this.model?.get(this.field?.name ?? '');

        this.refreshDisplayFromControl(false);

        if (!control) {
            this.cdr.markForCheck();
            return;
        }

        this.valueChangesSubscription = control.valueChanges.subscribe((value) => {
            this.updateDisplayValue(value);
            this.cdr.markForCheck();
        });

        this.cdr.markForCheck();
    }

    private refreshDisplayFromControl(shouldMarkForCheck = true): void {
        const value = this.model?.get(this.field?.name ?? '')?.value;

        if (value === this.lastRawValue) {
            return;
        }

        this.updateDisplayValue(value);

        if (shouldMarkForCheck) {
            this.cdr.markForCheck();
        }
    }

    private updateDisplayValue(value: unknown): void {
        this.lastRawValue = value;
        this.displayValue = this.getReadOnlyDisplayValue(value);
    }

    private getReadOnlyDisplayValue(value: unknown): string {
        if (value == null || value === '') {
            return '-';
        }

        if (Array.isArray(value)) {
            const parts = value
                .map((item) => this.stringifyItem(item))
                .filter((item) => item !== '');

            return parts.length > 0 ? parts.join(', ') : '-';
        }

        return this.stringifyItem(value) || '-';
    }

    private stringifyItem(value: unknown): string {
        if (value == null) {
            return '';
        }

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;

            return String(
                record['label']
                ?? record['nombre']
                ?? record['name']
                ?? record['descripcion']
                ?? record['uso']
                ?? record['agente']
                ?? ''
            ).trim();
        }

        return '';
    }
}
